import {
  daysFromNow,
  daysAgo,
  formatDateTime,
  nowMs,
} from '../../../shared/lib/relativeDates'

export type BalanceTypeKey = 'incoming' | 'available' | 'reserved' | 'onHold'
export type BalanceTypeLabel = 'Incoming' | 'Available' | 'Reserved' | 'On hold'

export type ActivityType =
  | 'Payment'
  | 'Settlement'
  | 'Reserve hold'
  | 'Reserve release'
  | 'Refund'
  | 'Void'
  | 'Chargeback'
  | 'Chargeback fee'
  | 'Chargeback reversal'
  | 'Processing fee'
  | 'Payout'
  | 'Review hold'
  | 'Review release'
  | 'Adjustment'

export type EntryStatus = 'Posted' | 'Pending' | 'Scheduled' | 'Held'

export interface BalanceEntry {
  // Unique balance transaction id for this individual ledger row (Stripe-style
  // bt_xxxxx). Stable identifier used to deep-link to the detail page.
  id: string
  // Internal ledger reference. All entries derived from the same source event
  // share this ref. Not shown in the main table — moved to a detail view.
  reference: string
  // Merchant-facing order reference, where applicable. Standalone events
  // (processing fee, payout) have no order ref.
  orderRef: string | null
  date: Date
  effectiveDate: string // "YYYY-MM-DD HH:mm"
  shortDate: string // "6 May"
  balanceTypeKey: BalanceTypeKey
  balanceType: BalanceTypeLabel
  activityType: ActivityType
  description: string
  customer: string
  amount: number // signed
  companyId: string
  status: EntryStatus
  // When this amount becomes (or did become) part of the Available balance.
  // null = not applicable (already in Available, or terminally held).
  availableOn: Date | null
  // When this amount is (or was) scheduled to be paid out to the merchant's
  // bank account. Only populated for credits to Available. null for entries
  // that don't translate into a payout (Incoming, Reserved, On hold, debits).
  payoutDate: Date | null
}

// ── Constants ─────────────────────────────────────────────────────────────

const ONE_DAY_MS = 24 * 60 * 60 * 1000
const RESERVE_PCT = 0.2
const SETTLEMENT_DAYS = 1
const RESERVE_DAYS = 30
const CHARGEBACK_FEE = 30

const BALANCE_LABEL: Record<BalanceTypeKey, BalanceTypeLabel> = {
  incoming: 'Incoming',
  available: 'Available',
  reserved: 'Reserved',
  onHold: 'On hold',
}

interface CompanyConfig {
  id: string
  label: string
  bankSuffix: string // last 4 digits shown in payout descriptions
}

const COMPANIES: CompanyConfig[] = [
  { id: 'submerchant-kl-001', label: 'KLCC', bankSuffix: '4521' },
  { id: 'submerchant-kl-002', label: 'BB', bankSuffix: '8830' },
  { id: 'submerchant-kl-003', label: 'MK', bankSuffix: '1147' },
  { id: 'submerchant-kl-004', label: 'Bangsar', bankSuffix: '6204' },
  { id: 'submerchant-kl-005', label: 'MV', bankSuffix: '3389' },
  { id: 'submerchant-kl-006', label: 'Damansara', bankSuffix: '7715' },
]

const CUSTOMER_NAMES = [
  'Aiman Tan',
  'Siti Rahman',
  'Lee Wei Ming',
  'Nur Aisyah',
  'Ravi Kumar',
  'Tan Hui Ling',
  'Ahmad Faizal',
  'Wong Mei Ying',
  'Priya Devi',
  'Kamarul Hisham',
  'Chong Kah Wai',
  'Farah Aziz',
  'Daniel Ng',
  'Hafiz Zulkifli',
  'Lim Jia Hao',
  'Suriani Abdullah',
  'Vincent Lau',
  'Iskandar Yusof',
  'Joyce Teh',
  'Norhayati Salleh',
]

// ── Utilities ─────────────────────────────────────────────────────────────

function mulberry32(seed: number) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0
    let t = seed
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const formatShortDate = (d: Date) =>
  d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })

const round2 = (n: number) => Math.round(n * 100) / 100

function statusFor(date: Date, balanceTypeKey: BalanceTypeKey): EntryStatus {
  const isFuture = date.getTime() > nowMs()
  if (balanceTypeKey === 'onHold') return 'Held'
  if (balanceTypeKey === 'incoming' && isFuture) return 'Pending'
  return isFuture ? 'Scheduled' : 'Posted'
}

// ── Payout schedule ───────────────────────────────────────────────────────
// Daily payout at 09:00 local time, per company. Spans past 14 → future 14
// days so any Available credit in the dataset can find a "next payout".

interface PayoutScheduleMap {
  [companyId: string]: Date[]
}

function buildPayoutSchedule(): PayoutScheduleMap {
  const map: PayoutScheduleMap = {}
  for (const company of COMPANIES) {
    const dates: Date[] = []
    for (let offset = -14; offset <= 14; offset++) {
      const d = offset >= 0 ? daysFromNow(offset) : daysAgo(-offset)
      d.setHours(9, 0, 0, 0)
      dates.push(d)
    }
    dates.sort((a, b) => a.getTime() - b.getTime())
    map[company.id] = dates
  }
  return map
}

function nextPayoutAfter(
  schedule: PayoutScheduleMap,
  companyId: string,
  ts: number,
): Date | null {
  const list = schedule[companyId] || []
  for (const d of list) {
    if (d.getTime() > ts) return d
  }
  return null
}

function bankSuffix(companyId: string): string {
  return COMPANIES.find((c) => c.id === companyId)?.bankSuffix ?? '0000'
}

// ── Description registry ─────────────────────────────────────────────────
// Plain-language labels. The Order ref column carries the ref; the customer
// name lives in the data model for a future row detail view.

function describe(
  activityType: ActivityType,
  balanceTypeKey: BalanceTypeKey,
  ctx: { bankSuffix?: string } = {},
): string {
  switch (activityType) {
    case 'Payment':
      return 'Customer payment'
    case 'Void':
      return 'Voided before settlement'
    case 'Settlement':
      return balanceTypeKey === 'available'
        ? 'Funds settled to Available'
        : 'Settled and cleared from Incoming'
    case 'Reserve hold':
      return '20% reserve held'
    case 'Reserve release':
      return balanceTypeKey === 'available'
        ? 'Reserve released to Available'
        : 'Reserve released'
    case 'Refund':
      return 'Refund to customer'
    case 'Chargeback':
      return 'Chargeback received'
    case 'Chargeback fee':
      return 'Chargeback fee'
    case 'Chargeback reversal':
      return 'Dispute won — funds returned'
    case 'Review hold':
      return balanceTypeKey === 'onHold' ? 'Under review' : 'Held for review'
    case 'Review release':
      return 'Review released'
    case 'Processing fee':
      return 'Daily processing fee'
    case 'Payout':
      return `Payout to bank ····${ctx.bankSuffix ?? '0000'}`
    case 'Adjustment':
      return 'Manual adjustment'
  }
}

// ── Event emitter ─────────────────────────────────────────────────────────

interface EmitContext {
  entries: BalanceEntry[]
  schedule: PayoutScheduleMap
}

type EmitInput = Omit<
  BalanceEntry,
  | 'effectiveDate'
  | 'shortDate'
  | 'balanceType'
  | 'status'
  | 'description'
  | 'payoutDate'
>

function emit(ctx: EmitContext, e: EmitInput) {
  // Compute payoutDate for credits to Available; otherwise null.
  let payoutDate: Date | null = null
  if (e.activityType === 'Payout') {
    payoutDate = e.date
  } else if (e.balanceTypeKey === 'available' && e.amount > 0) {
    payoutDate = nextPayoutAfter(ctx.schedule, e.companyId, e.date.getTime())
  }
  const id = `bt_${(ctx.entries.length + 1).toString(36).padStart(6, '0')}`
  ctx.entries.push({
    id,
    ...e,
    effectiveDate: formatDateTime(e.date),
    shortDate: formatShortDate(e.date),
    balanceType: BALANCE_LABEL[e.balanceTypeKey],
    status: statusFor(e.date, e.balanceTypeKey),
    description: describe(e.activityType, e.balanceTypeKey, {
      bankSuffix:
        e.activityType === 'Payout' ? bankSuffix(e.companyId) : undefined,
    }),
    payoutDate,
  })
}

// ── Source payment generator ──────────────────────────────────────────────

interface SourcePayment {
  ref: string // ledger ref (ch_xxx)
  orderRef: string // merchant-facing order ref (ord_xxx)
  date: Date
  amount: number
  customer: string
  companyId: string
  fate:
    | 'normal'
    | 'void'
    | 'refund'
    | 'chargeback'
    | 'chargeback-reversed'
    | 'review-hold'
}

function generateSourcePayments(rand: () => number): SourcePayment[] {
  const sources: SourcePayment[] = []
  let n = 1
  let ordN = 100001
  for (let dayOffset = -7; dayOffset <= 12; dayOffset++) {
    const txnsThisDay = 6 + Math.floor(rand() * 5)
    for (let i = 0; i < txnsThisDay; i++) {
      const date = dayOffset >= 0 ? daysFromNow(dayOffset) : daysAgo(-dayOffset)
      date.setHours(Math.floor(rand() * 24))
      date.setMinutes(Math.floor(rand() * 60))
      const company = COMPANIES[Math.floor(rand() * COMPANIES.length)]
      const customer =
        CUSTOMER_NAMES[Math.floor(rand() * CUSTOMER_NAMES.length)]
      const amount = round2(40 + rand() * 1200)

      let fate: SourcePayment['fate'] = 'normal'
      if (dayOffset < 0) {
        const r = rand()
        if (r < 0.03) fate = 'void'
        else if (r < 0.08) fate = 'refund'
        else if (r < 0.1) fate = 'chargeback'
        else if (r < 0.115) fate = 'chargeback-reversed'
        else if (r < 0.13) fate = 'review-hold'
      }

      sources.push({
        ref: `ch_${(n++).toString(36).padStart(5, '0')}`,
        orderRef: `ord_${(ordN++).toString()}`,
        date,
        amount,
        customer,
        companyId: company.id,
        fate,
      })
    }
  }
  return sources
}

// ── Lifecycle expansion ───────────────────────────────────────────────────

function expandSourcePayment(
  src: SourcePayment,
  ctx: EmitContext,
  rand: () => number,
) {
  const settlementDate = new Date(
    src.date.getTime() + SETTLEMENT_DAYS * ONE_DAY_MS,
  )
  settlementDate.setHours(0, 0, 0, 0)

  const releaseDate = new Date(
    settlementDate.getTime() + RESERVE_DAYS * ONE_DAY_MS,
  )

  const reserveAmount = round2(src.amount * RESERVE_PCT)
  const availableAmount = round2(src.amount - reserveAmount)

  // 1. Payment lands in Incoming
  emit(ctx, {
    reference: src.ref,
    orderRef: src.orderRef,
    date: src.date,
    balanceTypeKey: 'incoming',
    activityType: 'Payment',
    customer: src.customer,
    amount: src.amount,
    companyId: src.companyId,
    availableOn: settlementDate,
  })

  if (src.fate === 'void') {
    const voidDate = new Date(
      src.date.getTime() + (1 + rand() * 6) * 60 * 60 * 1000,
    )
    emit(ctx, {
      reference: src.ref,
      orderRef: src.orderRef,
      date: voidDate,
      balanceTypeKey: 'incoming',
      activityType: 'Void',
      customer: src.customer,
      amount: -src.amount,
      companyId: src.companyId,
      availableOn: null,
    })
    return
  }

  // 2. Settlement: Incoming −, Available +(80%), Reserved +(20%)
  emit(ctx, {
    reference: src.ref,
    orderRef: src.orderRef,
    date: settlementDate,
    balanceTypeKey: 'incoming',
    activityType: 'Settlement',
    customer: src.customer,
    amount: -src.amount,
    companyId: src.companyId,
    availableOn: null,
  })
  emit(ctx, {
    reference: src.ref,
    orderRef: src.orderRef,
    date: settlementDate,
    balanceTypeKey: 'available',
    activityType: 'Settlement',
    customer: src.customer,
    amount: availableAmount,
    companyId: src.companyId,
    availableOn: null,
  })
  emit(ctx, {
    reference: src.ref,
    orderRef: src.orderRef,
    date: settlementDate,
    balanceTypeKey: 'reserved',
    activityType: 'Reserve hold',
    customer: src.customer,
    amount: reserveAmount,
    companyId: src.companyId,
    availableOn: releaseDate,
  })

  // 3. Reserve release at T+31
  emit(ctx, {
    reference: src.ref,
    orderRef: src.orderRef,
    date: releaseDate,
    balanceTypeKey: 'reserved',
    activityType: 'Reserve release',
    customer: src.customer,
    amount: -reserveAmount,
    companyId: src.companyId,
    availableOn: null,
  })
  emit(ctx, {
    reference: src.ref,
    orderRef: src.orderRef,
    date: releaseDate,
    balanceTypeKey: 'available',
    activityType: 'Reserve release',
    customer: src.customer,
    amount: reserveAmount,
    companyId: src.companyId,
    availableOn: null,
  })

  // 4. Optional post-settlement events
  if (src.fate === 'refund') {
    const refundPct = 0.3 + rand() * 0.7
    const refundAmount = round2(src.amount * refundPct)
    const refundDate = new Date(
      settlementDate.getTime() + (2 + rand() * 3) * ONE_DAY_MS,
    )
    refundDate.setHours(Math.floor(rand() * 24))
    refundDate.setMinutes(Math.floor(rand() * 60))
    emit(ctx, {
      reference: src.ref,
      orderRef: src.orderRef,
      date: refundDate,
      balanceTypeKey: 'available',
      activityType: 'Refund',
      customer: src.customer,
      amount: -refundAmount,
      companyId: src.companyId,
      availableOn: null,
    })
  } else if (src.fate === 'chargeback' || src.fate === 'chargeback-reversed') {
    const cbDate = new Date(
      settlementDate.getTime() + (5 + rand() * 10) * ONE_DAY_MS,
    )
    cbDate.setHours(Math.floor(rand() * 24))
    cbDate.setMinutes(Math.floor(rand() * 60))
    emit(ctx, {
      reference: src.ref,
      orderRef: src.orderRef,
      date: cbDate,
      balanceTypeKey: 'available',
      activityType: 'Chargeback',
      customer: src.customer,
      amount: -src.amount,
      companyId: src.companyId,
      availableOn: null,
    })
    emit(ctx, {
      reference: src.ref,
      orderRef: src.orderRef,
      date: cbDate,
      balanceTypeKey: 'available',
      activityType: 'Chargeback fee',
      customer: src.customer,
      amount: -CHARGEBACK_FEE,
      companyId: src.companyId,
      availableOn: null,
    })
    if (src.fate === 'chargeback-reversed') {
      const revDate = new Date(cbDate.getTime() + (7 + rand() * 7) * ONE_DAY_MS)
      if (revDate.getTime() <= nowMs()) {
        emit(ctx, {
          reference: src.ref,
          orderRef: src.orderRef,
          date: revDate,
          balanceTypeKey: 'available',
          activityType: 'Chargeback reversal',
          customer: src.customer,
          amount: src.amount,
          companyId: src.companyId,
          availableOn: null,
        })
      }
    }
  } else if (src.fate === 'review-hold') {
    const holdDate = new Date(
      settlementDate.getTime() + (1 + rand() * 4) * ONE_DAY_MS,
    )
    holdDate.setHours(Math.floor(rand() * 24))
    holdDate.setMinutes(Math.floor(rand() * 60))
    if (holdDate.getTime() <= nowMs()) {
      emit(ctx, {
        reference: src.ref,
        orderRef: src.orderRef,
        date: holdDate,
        balanceTypeKey: 'available',
        activityType: 'Review hold',
        customer: src.customer,
        amount: -src.amount,
        companyId: src.companyId,
        availableOn: null,
      })
      emit(ctx, {
        reference: src.ref,
        orderRef: src.orderRef,
        date: holdDate,
        balanceTypeKey: 'onHold',
        activityType: 'Review hold',
        customer: src.customer,
        amount: src.amount,
        companyId: src.companyId,
        availableOn: null,
      })
    }
  }
}

// ── Standalone events ────────────────────────────────────────────────────

function generateProcessingFees(ctx: EmitContext, rand: () => number) {
  let n = 1
  for (let dayOffset = -7; dayOffset <= 0; dayOffset++) {
    for (const company of COMPANIES) {
      const date = daysAgo(-dayOffset)
      date.setHours(23, 30, 0, 0)
      const fee = round2(20 + rand() * 80)
      emit(ctx, {
        reference: `fee_${(n++).toString(36).padStart(5, '0')}`,
        orderRef: null,
        date,
        balanceTypeKey: 'available',
        activityType: 'Processing fee',
        customer: '—',
        amount: -fee,
        companyId: company.id,
        availableOn: null,
      })
    }
  }
}

function generatePayouts(ctx: EmitContext, rand: () => number) {
  let n = 1
  // Past payouts only — future payouts are speculative until they happen.
  // The "Payout date" column on Available credits already conveys the
  // upcoming schedule, so we don't need to pre-create future Payout rows.
  for (let dayOffset = -7; dayOffset <= -1; dayOffset++) {
    for (const company of COMPANIES) {
      if (rand() < 0.4) continue
      const date = daysAgo(-dayOffset)
      date.setHours(9, 0, 0, 0)
      const amount = round2(2000 + rand() * 6000)
      emit(ctx, {
        reference: `po_${(n++).toString(36).padStart(5, '0')}`,
        orderRef: null,
        date,
        balanceTypeKey: 'available',
        activityType: 'Payout',
        customer: '—',
        amount: -amount,
        companyId: company.id,
        availableOn: null,
      })
    }
  }
}

// ── Public API ────────────────────────────────────────────────────────────

let cachedEntries: BalanceEntry[] | null = null

export function getBalanceEntries(): BalanceEntry[] {
  if (cachedEntries) return cachedEntries
  const rand = mulberry32(20260506)
  const ctx: EmitContext = { entries: [], schedule: buildPayoutSchedule() }

  for (const src of generateSourcePayments(rand)) {
    expandSourcePayment(src, ctx, rand)
  }
  generateProcessingFees(ctx, rand)
  generatePayouts(ctx, rand)

  ctx.entries.sort((a, b) => b.date.getTime() - a.date.getTime())
  cachedEntries = ctx.entries
  return ctx.entries
}

export interface DailyChartPoint {
  date: string
  amount: number
  count: number
}

/**
 * Which date field should drive a bucket's chart and "go to Activity"
 * deep-link? The Summary labels each bucket with a forward-looking promise
 * ("Becoming available", "Upcoming reserve releases", "Upcoming payout
 * schedule") so the chart must group by *that* date, not by when the
 * entry was first recorded.
 */
export function getGroupingFieldForBucket(
  balanceTypeKey: BalanceTypeKey,
): 'availableOn' | 'payoutDate' | 'date' {
  switch (balanceTypeKey) {
    case 'incoming':
      return 'availableOn' // T+1 settlement → when funds become Available
    case 'reserved':
      return 'availableOn' // T+31 → when reserve releases to Available
    case 'available':
      return 'payoutDate' // when funds leave Available to bank
    case 'onHold':
      return 'date' // no forward date — just when it was held
  }
}

function pickDate(
  e: BalanceEntry,
  field: 'availableOn' | 'payoutDate' | 'date',
): Date | null {
  if (field === 'date') return e.date
  return e[field]
}

export function getChartDataForCompany(
  balanceTypeKey: BalanceTypeKey,
  companyType: 'master' | 'submerchant',
  companyId: string,
): DailyChartPoint[] {
  const field = getGroupingFieldForBucket(balanceTypeKey)
  // Charts in Balance Summary are forward-looking promises ("Becoming
  // available", "Upcoming reserve releases", "Upcoming payout schedule").
  // Exclude any entries whose grouping date falls before today's start so
  // already-settled / already-paid-out days don't appear.
  const startOfToday = (() => {
    const d = new Date(nowMs())
    d.setHours(0, 0, 0, 0)
    return d.getTime()
  })()
  const all = getBalanceEntries()
  const filtered = all.filter((e) => {
    if (e.balanceTypeKey !== balanceTypeKey) return false
    if (e.amount <= 0) return false
    if (companyType === 'submerchant' && e.companyId !== companyId) return false
    const groupDate = pickDate(e, field)
    if (!groupDate) return false
    // On hold has no forward-looking date semantics — don't filter it.
    if (balanceTypeKey !== 'onHold' && groupDate.getTime() < startOfToday)
      return false
    return true
  })
  const byDay = new Map<string, { amount: number; count: number; ts: number }>()
  for (const e of filtered) {
    const d = pickDate(e, field)!
    const key = formatShortDate(d)
    const cur = byDay.get(key)
    if (cur) {
      cur.amount += e.amount
      cur.count += 1
    } else {
      byDay.set(key, { amount: e.amount, count: 1, ts: d.getTime() })
    }
  }
  return Array.from(byDay.entries())
    .map(([date, v]) => ({
      date,
      amount: round2(v.amount),
      count: v.count,
      ts: v.ts,
    }))
    .sort((a, b) => a.ts - b.ts)
    .map(({ date, amount, count }) => ({ date, amount, count }))
}

export function getEntryById(id: string): BalanceEntry | undefined {
  return getBalanceEntries().find((e) => e.id === id)
}

/**
 * Returns all entries sharing the same source reference (ch_xxx / fee_xxx /
 * po_xxx) — i.e. every ledger row generated by the same source event.
 * Sorted oldest → newest so the detail page can render a natural timeline.
 */
export function getRelatedEntries(reference: string): BalanceEntry[] {
  return getBalanceEntries()
    .filter((e) => e.reference === reference)
    .slice()
    .sort((a, b) => a.date.getTime() - b.date.getTime())
}

/**
 * Find the Payout entry whose schedule covers a given Available credit.
 * Returns the next Payout row (po_xxx) for the same company at or after
 * the credit's payoutDate. Used by the detail page to surface the Payout ID
 * for non-payout entries that contribute to a payout.
 */
export function getPayoutForEntry(entry: BalanceEntry): BalanceEntry | null {
  if (!entry.payoutDate) return null
  if (entry.activityType === 'Payout') return entry
  const all = getBalanceEntries()
  const target = entry.payoutDate.getTime()
  // Match within ±12h of the scheduled payout time, same company
  const window = 12 * 60 * 60 * 1000
  return (
    all.find(
      (e) =>
        e.activityType === 'Payout' &&
        e.companyId === entry.companyId &&
        Math.abs(e.date.getTime() - target) <= window,
    ) || null
  )
}

export function getTotalForCompany(
  balanceTypeKey: BalanceTypeKey,
  companyType: 'master' | 'submerchant',
  companyId: string,
): number {
  const points = getChartDataForCompany(balanceTypeKey, companyType, companyId)
  return round2(points.reduce((s, p) => s + p.amount, 0))
}

/**
 * Daily payout schedule for the Available bucket — aggregates Available
 * credits by their scheduled payoutDate. This is what the Balance Summary
 * "Upcoming payout schedule" section should reflect.
 */
export function getPayoutScheduleForCompany(
  companyType: 'master' | 'submerchant',
  companyId: string,
): DailyChartPoint[] {
  const startOfToday = (() => {
    const d = new Date(nowMs())
    d.setHours(0, 0, 0, 0)
    return d.getTime()
  })()
  const all = getBalanceEntries()
  const byDay = new Map<string, { amount: number; count: number; ts: number }>()
  for (const e of all) {
    if (!e.payoutDate) continue
    if (e.balanceTypeKey !== 'available') continue
    if (e.amount <= 0) continue
    if (companyType === 'submerchant' && e.companyId !== companyId) continue
    const d = e.payoutDate
    if (d.getTime() < startOfToday) continue
    const key = formatShortDate(d)
    const cur = byDay.get(key)
    if (cur) {
      cur.amount += e.amount
      cur.count += 1
    } else {
      byDay.set(key, { amount: e.amount, count: 1, ts: d.getTime() })
    }
  }
  return Array.from(byDay.entries())
    .map(([date, v]) => ({
      date,
      amount: round2(v.amount),
      count: v.count,
      ts: v.ts,
    }))
    .sort((a, b) => a.ts - b.ts)
    .map(({ date, amount, count }) => ({ date, amount, count }))
}
